import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, finalize, map, of, shareReplay, tap } from 'rxjs';
import { format } from 'date-fns';
import { environment } from '../../../../environments/environment';
import { Result } from '../../../core/models/result.model';
import { AlertService } from '../../../core/alerts/services/alert.service';
import { I18nService } from '../../../core/i18n/services/i18n.service';
import { ConsultaAuditoriaFiltro, RegistroAuditoria, TelaAuditavel } from '../models/registro-auditoria.model';

@Injectable({ providedIn: 'root' })
export class AuditoriaService {
  private readonly http = inject(HttpClient);
  private readonly alerts = inject(AlertService);
  private readonly i18n = inject(I18nService);
  private readonly url = `${environment.apiUrl.replace(/\/$/, '')}/api/auditorias`;
  private readonly telasSignal = signal<TelaAuditavel[]>([]);
  private readonly catalogoCarregadoSignal = signal(false);
  private catalogoRequest?: Observable<TelaAuditavel[]>;

  readonly telas = this.telasSignal.asReadonly();
  readonly catalogoCarregado = this.catalogoCarregadoSignal.asReadonly();

  carregarTelas(): Observable<TelaAuditavel[]> {
    if (this.catalogoCarregado()) return of(this.telas());
    if (!this.catalogoRequest) {
      this.catalogoRequest = this.http.get<Result<TelaAuditavel[]>>(`${this.url}/telas`).pipe(
        map(result => this.obterDados(result)),
        tap(telas => {
          this.telasSignal.set(telas);
          this.catalogoCarregadoSignal.set(true);
        }),
        finalize(() => { this.catalogoRequest = undefined; }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.catalogoRequest;
  }

  consultar(filtro: ConsultaAuditoriaFiltro): Observable<RegistroAuditoria[]> {
    let params = new HttpParams()
      .set('tela', filtro.tela)
      .set('dataInicio', format(filtro.dataInicio, 'yyyy-MM-dd'));
    if (filtro.registro) params = params.set('registro', filtro.registro);
    if (filtro.dataFim) params = params.set('dataFim', format(filtro.dataFim, 'yyyy-MM-dd'));
    return this.http.get<Result<RegistroAuditoria[]>>(this.url, { params }).pipe(
      map(result => this.obterDados(result))
    );
  }

  private obterDados<T>(result: Result<T>): T {
    if (result.isSuccess && result.data != null) return result.data;
    // Erros HTTP já são apresentados pelo interceptor global; falhas no envelope 200 não passam por ele.
    if (result.errors?.length) this.alerts.errorList(result.errors);
    else this.alerts.error(result.message || this.i18n.t('FEATURES.AUDITORIA.ERRO_RESPOSTA'));
    throw new Error('Resposta de auditoria sem dados de sucesso.');
  }
}
